/**
 * Custom Scrollbar Component
 * A reusable horizontal scrollbar that syncs with scrollable containers
 */
class CustomScrollbar {
  constructor(container, scrollbar) {
    this.container = container;
    this.scrollbar = scrollbar;
    this.track = scrollbar.querySelector('.pd-custom-scrollbar__track');
    this.thumb = scrollbar.querySelector('.pd-custom-scrollbar__thumb');
    
    this.isDragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    
    this.init();
  }

  init() {
    // Check if content is scrollable
    this.checkScrollable();
    
    // Bind events
    this.container.addEventListener('scroll', () => this.handleScroll());
    this.thumb.addEventListener('mousedown', (e) => this.handleDragStart(e));
    this.thumb.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: false });
    
    document.addEventListener('mousemove', (e) => this.handleDragMove(e));
    document.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: false });
    
    document.addEventListener('mouseup', () => this.handleDragEnd());
    document.addEventListener('touchend', () => this.handleDragEnd());
    
    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.checkScrollable();
        this.updateScrollbar();
      }, 150);
    });
    
    // Initial update
    this.updateScrollbar();
  }

  checkScrollable() {
    const isScrollable = this.container.scrollWidth > this.container.clientWidth;
    
    if (isScrollable) {
      this.scrollbar.style.display = 'block';
    } else {
      this.scrollbar.style.display = 'none';
    }
    
    return isScrollable;
  }

  updateScrollbar() {
    if (!this.checkScrollable()) return;
    
    const containerWidth = this.container.clientWidth;
    const contentWidth = this.container.scrollWidth;
    const scrollLeft = this.container.scrollLeft;
    
    // Calculate thumb width as a percentage of track width
    const thumbWidthPercent = (containerWidth / contentWidth) * 100;
    this.thumb.style.width = `${thumbWidthPercent}%`;
    
    // Calculate thumb position
    const scrollPercent = scrollLeft / (contentWidth - containerWidth);
    const maxThumbPosition = 100 - thumbWidthPercent;
    const thumbPosition = scrollPercent * maxThumbPosition;
    
    this.thumb.style.left = `${thumbPosition}%`;
  }

  handleScroll() {
    if (!this.isDragging) {
      requestAnimationFrame(() => this.updateScrollbar());
    }
  }

  handleDragStart(e) {
    this.isDragging = true;
    this.thumb.classList.add('is-dragging');
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    this.startX = clientX;
    this.startScrollLeft = this.container.scrollLeft;
    
    e.preventDefault();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - this.startX;
    
    const trackWidth = this.track.clientWidth;
    const containerWidth = this.container.clientWidth;
    const contentWidth = this.container.scrollWidth;
    
    // Calculate scroll distance based on drag distance
    const scrollRatio = (contentWidth - containerWidth) / trackWidth;
    const scrollDelta = deltaX * scrollRatio;
    
    this.container.scrollLeft = this.startScrollLeft + scrollDelta;
    
    e.preventDefault();
  }

  handleDragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.thumb.classList.remove('is-dragging');
  }
}

/**
 * Auto-initialize all custom scrollbars on the page
 */
function initCustomScrollbars() {
  const scrollbars = document.querySelectorAll('.pd-custom-scrollbar');
  
  scrollbars.forEach(scrollbar => {
    const containerId = scrollbar.getAttribute('data-scrollbar-for');
    const container = document.getElementById(containerId);
    
    if (container) {
      new CustomScrollbar(container, scrollbar);
    } else {
      console.warn(`Custom scrollbar: Container with id "${containerId}" not found`);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomScrollbars);
} else {
  // DOM is already loaded
  initCustomScrollbars();
}

// Re-initialize on Shopify section events (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initCustomScrollbars);
  document.addEventListener('shopify:section:reorder', initCustomScrollbars);
}

